

<script setup>
import { onMounted, reactive, computed, ref } from 'vue';
import { TheChessboard } from 'vue3-chessboard';
import moveSoundSrc from '@/assets/move-self.mp3';
import wrongSoundSrc from '@/assets/illegal.mp3';
import 'vue3-chessboard/style.css';
import NavBar from './NavBar.vue';
import { usePuzzleStore } from '@/stores/puzzleStore';
//import whiteDot from '@/assets/white-dot.svg';
import chessKing from '@/assets/chess-king-b.svg';
import Toggle from '@vueform/toggle';
import axios from 'axios';
import { useRouter } from 'vue-router';



const puzzleStore = usePuzzleStore();

const currentPuzzle = computed(() => puzzleStore.currentPuzzle);

// Puzzle Object for sidebar
const puzzleData = reactive({
  playing_side: '',
  match_rated: '',
  time_class: '',
  white_name: '',
  white_rating: '',
  black_name: '',
  black_rating: '',
  white_id: '',
  black_id: '',
  lichessURL: '',
});

let boardAPI = null;
const moveSound = new Audio(moveSoundSrc);
const wrongSound = new Audio(wrongSoundSrc);

// Timer logic

const startTime = ref(null);
const elapsedTime = ref("00:00.000"); // MM:SS.mmm
let timerInterval = null;
let timeStopped = null;
let stoppedDuration = 0;

function startTimer() {
  if (!startTime.value) {
    startTime.value = new Date();
    stoppedDuration = 0;
  } else if (timeStopped) {
    stoppedDuration += new Date() - timeStopped;
  }

  timerInterval = setInterval(updateTimer, 10);
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
    timeStopped = new Date();
  }
}

function resetTimer() {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  startTime.value = null;
  stoppedDuration = 0;
  elapsedTime.value = "00:00.000";
}

function updateTimer() {
  if (!startTime.value) return;

  const currentTime = new Date();
  const timeElapsed = new Date(currentTime - startTime.value - stoppedDuration);

  const min = timeElapsed.getUTCMinutes();
  const sec = timeElapsed.getUTCSeconds();
  const ms = timeElapsed.getUTCMilliseconds();

  elapsedTime.value = `${zeroPrefix(min, 2)}:${zeroPrefix(sec, 2)}.${zeroPrefix(ms, 3)}`;
}

function zeroPrefix(num, digit) {
  return String(num).padStart(digit, "0");
}

// Board configuration

const boardConfig = reactive({
  fen: '',
  orientation: 'white',
  turnColor: 'white',
  coordinates: true,
  drawable: {
    enabled: true,
    visible: true,
    defaultSnapToValidMove: true,
    eraseOnClick: true,
    shapes: [],
    autoShapes: [],
    brushes: {
      green: { key: 'g', color: '#2e2e2e', opacity: 1, lineWidth: 10 },
      red: { key: 'r', color: '#2e2e2e', opacity: 1, lineWidth: 10 },
      blue: { key: 'b', color: '#2e2e2e', opacity: 1, lineWidth: 10 },
      yellow: { key: 'y', color: '#2e2e2e', opacity: 1, lineWidth: 10 },
      paleBlue: { key: 'pb', color: '#2e2e2e', opacity: 0.4, lineWidth: 15 },
      paleGreen: { key: 'pg', color: '#2e2e2e', opacity: 0.4, lineWidth: 15 },
      paleGrey: { key: 'pgr', color: '#2e2e2e', opacity: 0.69, lineWidth: 15 }
    },
  },
});

//Move handling

let currentPuzzleIndex = 0;
let isOpponentMove = false;

function playMoveSound() {
  moveSound.currentTime = 0;
  moveSound.play().catch((error) => {
    console.error('Unable to play move sound:', error);
  });
}

function playWrongSound() {
  wrongSound.currentTime = 0;
  wrongSound.volume = 0.7;
  wrongSound.play().catch((error) => {
    console.error('Unable to play wrong move sound:', error);
  });
}

function handleMove(move) {

  if (isOpponentMove) {
    // Reset flag and allow the opponent's move
    isOpponentMove = false;
    playMoveSound();
    return true;
  }

  // Construct a UCI move string (e.g., "f7h7") from the move object.
  const moveUCI = move.from + move.to;
  const expectedMove = currentPuzzle.value.sequence[currentPuzzleIndex];

  if (moveUCI === expectedMove) {
    playMoveSound();
    currentPuzzleIndex++;

    if (currentPuzzleIndex < currentPuzzle.value.sequence.length) {
      const opponentMove = currentPuzzle.value.sequence[currentPuzzleIndex];
      setTimeout(() => {
        const from = opponentMove.substring(0, 2);
        const to = opponentMove.substring(2, 4);

        // Mark as opponent move before executing
        isOpponentMove = true;
        boardAPI.move({ from, to });
      }, 500);

      currentPuzzleIndex++;
    } else {
      stopTimer();
      puzzleStore.markPuzzleSolved(puzzleWon);
      if (puzzleWon) {
        const timeTaken = new Date() - startTime.value;
        const timeBonus = calculateTimeBonus(timeTaken);
        totalScore.value += 100 + timeBonus;
        appendScore(totalScore.value);
      }
      else {
        totalScore.value -= 100;
        appendScore(totalScore.value);
        puzzleWon = true;
      }
      currentPuzzleIndex = 0;
      if (autoForwardEnabled.value) {
        setTimeout(() => {
          nextPuzzle();
        }, 1000);
      } else {
        finished.value = true;
      }
    }

  } else {
    playWrongSound();
    puzzleWon = false;
    setTimeout(() => {
      boardAPI.undoLastMove();
    }, 250);

    return false;  // Prevent incorrect move
  }
}


// Button functions

function loadPuzzle() {
  finished.value = false;
  currentPuzzleIndex = 0;
  // If no puzzle is available, do nothing or show an alert
  if (!currentPuzzle.value) return;

  /////////////////////////////
  puzzleData.playing_side = currentPuzzle.value.playing_side;
  puzzleData.match_rated = currentPuzzle.value.match_rated;
  puzzleData.time_class = currentPuzzle.value.match_time_class;
  puzzleData.white_name = currentPuzzle.value.match_players.white.user.name;
  puzzleData.white_rating = currentPuzzle.value.match_players.white.rating;
  puzzleData.black_name = currentPuzzle.value.match_players.black.user.name;
  puzzleData.black_rating = currentPuzzle.value.match_players.black.rating;
  puzzleData.white_id = currentPuzzle.value.match_players.white.user['@id'];
  puzzleData.black_id = currentPuzzle.value.match_players.black.user['@id'];
  puzzleData.lichessURL = currentPuzzle.value.lichess_url;
  /////////////////////////////

  console.log('Loading puzzle:', currentPuzzle.value);

  // Set config

  const playSide = currentPuzzle.value.playing_side;

  boardAPI?.resetBoard();

  boardConfig.orientation = playSide;
  boardConfig.turnColor = playSide;

  console.log('Player side:', currentPuzzle.value.playing_side);

  

  // Set board to the puzzle's FEN
  if (boardAPI) {
    boardAPI.setPosition(currentPuzzle.value.fen);
  }

  resetTimer();
  startTimer();
}


function setShapesSmall() {
  puzzleWon = false;
  const expectedMove = currentPuzzle.value.sequence[currentPuzzleIndex];

  boardAPI?.setShapes([
    { orig: expectedMove.substring(0,2), brush: 'paleGrey' }
  ]);
}

function setShapesBig() {
  puzzleWon = false;
  const expectedMove = currentPuzzle.value.sequence[currentPuzzleIndex];

  boardAPI?.setShapes([
    { orig: expectedMove.substring(0,2), dest: expectedMove.substring(2,4), brush: 'paleGrey' }
  ]);
}



// Lifecycle
onMounted(() => {

  loadPuzzle();

});



// Chart logic
const totalScore = ref(0);
let puzzleWon = true;

const series = ref([
  {
    name: 'Score',
    data: [0], // Sample data points
  },
]);

const chartOptions = ref({
  chart: {
    id: 'score-chart',
    type: 'area',
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
    foreColor: '#EBECD0',
  },
  tooltip: {
    theme: 'dark',
  },
  dataLabels: {
    enabled: false,
  },
  title: {
    text: 'Solving Performance',
    align: 'left',
    style: {
      fontSize:  '18px',
      fontFamily:  'Roboto Mono',
      color:  '#EBECD0'
    },
  },
  stroke: {
    width: 2,
    colors:['#EBECD0'],
  },
  markers: {
    size: 0,
  },

  plotOptions: {
    line: {
      colors: {
        threshold: 0,
        colorAboveThreshold: '#BB86FC',
        colorBelowThreshold: '#fc868e',
      },
    },
  },
});

function appendScore(score) {
  series.value[0].data.push(score);
}

function calculateTimeBonus(timeTaken) {
  // Convert time taken to seconds
  const timeInSeconds = timeTaken / 1000;

  // If solved in under 60 seconds, calculate a proportional bonus up to 50 points
  if (timeInSeconds < 60) {
    return Math.round((60 - timeInSeconds) * (50 / 60));
  }

  // No bonus if it took 60 seconds or more
  return 0;
}

const autoForwardEnabled = ref(false);
const finished = ref(false);

async function saveToMongo(rating) {

  nextPuzzle();
  try {

    const puzzleWithRating = {
      ...currentPuzzle.value, 
      user_rating: rating,
    };

    console.log("puzzle with rating: ",puzzleWithRating);
    
    const response = await axios.post(
      `${process.env.VUE_APP_API_BASE_URL}/puzzles`, 
      puzzleWithRating
    );

    console.log('Puzzle saved successfully:', response.data);
  } catch (error) {
    console.error('Error saving puzzle:', error);
    alert('Error saving puzzle.');
  }
}

function openLichessPosition() {
  window.open(puzzleData.lichessURL, '_blank');
}


const router = useRouter(); // Use Vue Router inside script setup

function nextPuzzle() {
  const hasMorePuzzles = puzzleStore.nextPuzzle(); // Try to load the next puzzle

  if (!hasMorePuzzles) {
    console.log("No more puzzles left, redirecting...");
    router.push({ name: 'analysis' }); // Redirect to analysis page
  } else {
    loadPuzzle(); // Load the new puzzle if available
  }
}

</script>

<template>
  <div class="d-flex flex-column vh-100 primary-font">
    <div class="d-flex flex-column justify-content-center align-items-center"> 
      <div class="d-flex flex-grow-1 justify-content-center align-items-center">
        <div class="d-flex flex-column vh-100">
          <!-- NAVBAR-->
          <div class="d-flex justify-content-center">
            <div class="w-100 w-xxl-75">
              <NavBar />
            </div>
          </div>

          <div class="d-flex flex-column w-100 flex-xxl-row">
            <!-- Chessboard -->
            <div class="w-100 w-xxl-75">
              <TheChessboard
                :board-config="boardConfig"
                reactive-config
                @board-created="(api) => (boardAPI = api)"
                @move="handleMove"
              />
            </div>
      
            <!-- Sidebar -->
            <div class="w-100 w-xxl-25 rounded p-4 ms-xxl-4 sidebar d-flex flex-column">
              

              <div class="text-center mb-3 bgr-dark rounded p-4 " id = "clock">
                <h1 class="m-0 timer">{{ elapsedTime }}</h1>
              </div>


              <div class="text-center mb-3 bgr-dark rounded p-4 " v-if="!finished">
                <font-awesome-icon :icon="['fas', 'chess-king']" class="kingIcon" v-if= "puzzleData.playing_side === 'white'"/>
                <img :src="chessKing" alt="Chess King Icon" class="kingIcon" v-else/>
                <h4 class="m-0 card-title">
                  Find the best move for
                  {{ puzzleData.playing_side }}
                </h4>
              </div>


              <div class="text-center mb-3 bgr-dark rounded p-4">
                <h4 class="card-title">From {{ puzzleData.match_rated ? 'rated' : 'unrated' }} {{ puzzleData.time_class }} game</h4>
                <hr class="border border-light">
                <div class="d-flex justify-content-center align-items-center">
                  <div class="text-start d-flex flex-column">
                    <a :href="puzzleData.white_id" target="_blank" class="text-decoration-none card-text clr-w">
                    &#x25CF; <strong>{{ puzzleData.white_name }}</strong>
                    ({{ puzzleData.white_rating }})
                    </a>
                    <a :href="puzzleData.black_id" target="_blank" class="text-decoration-none card-text clr-w">
                    &#x26AC; <strong>{{ puzzleData.black_name }}</strong>
                    ({{ puzzleData.black_rating }})
                    </a>
                  </div>
                </div>
              </div>

              <div class="text-center mb-3 bgr-dark rounded p-4">
                <apex-chart
                  :height="200"
                  :options="chartOptions"
                  :series="series"
                />
              </div>


              <div class="text-center mb-3 bgr-dark rounded p-4 d-flex justify-content-center" v-if="!finished">
                <div>
                  <Toggle
                    class="clr-w bgr-dark"
                    v-model="autoForwardEnabled"
                    on-label="On"
                    off-label="Off"
                  />
                </div>
                <h5 class="ps-4 m0 card-title">Auto forward puzzles</h5>
              </div>  
            
      
              <div class="rating-container d-flex flex-column align-items-center p-4 bgr-dark rounded flex-grow-1" v-if="finished">
                <h4 class="text-center">Rate the puzzle</h4>
                <hr class="w-100 border-light my-2" />

                <!-- Thumbs buttons -->
                <div class="d-flex w-100 gap-2 flex-grow-1">
                  <button @click="saveToMongo(1)" class="btn btn-outline-success flex-grow-1 thumbs-btn">
                    <font-awesome-icon :icon="['far', 'thumbs-up']" size="3x"/>
                  </button>
                  <button @click="saveToMongo(0)" class="btn btn-outline-danger flex-grow-1 thumbs-btn">
                    <font-awesome-icon :icon="['far', 'thumbs-down']" size="3x"/>
                  </button>
                </div>

                <!-- Bottom Buttons -->
                <div class="d-flex w-100 gap-2 mt-3">
                  <button @click="openLichessPosition" class="btn btn-dark w-25 small-btn">
                    <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                  </button>
                  <button @click="nextPuzzle" class="btn btn-primary w-75 flex-grow-3 big-btn">
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                  </button>
                </div>
              </div>
              <div class="flex-grow-1 align-items-center bgr-dark rounded p-4" v-else>
                <div class="d-flex flex-xxl-column flex-row h-100">
                  <div class="flex-fill d-flex justify-content-center align-items-center">
                    <button @click="setShapesSmall" class="btn hintButtons w-100 h-100">
                      <h4 class="card-max">HINT</h4>
                    </button>
                  </div>
                  <div class="flex-fill d-flex justify-content-center align-items-center">
                    <button @click="setShapesBig" class="btn hintButtons py-2 w-100 h-100">
                        <h4 class="card-max">VIEW THE SOLUTION</h4>
                    </button>
                  </div>
                </div>
              </div>
            </div>  
          </div>
        </div>
      </div>
    </div>
  </div>  
</template>


<style scoped>

.btn-primary {
  background-color: #BB86FC;
  border-color: #BB86FC;
}

.btn-primary:hover {
  background-color: #913bfa;
  border-color: #913bfa;
}

.clr-w {
  color: #EBECD0;
}

.btn-outline-success {
  border: none;
}


.btn-outline-danger {
  border: none;
}


.timer {
  font-size: 3.5vh;
}

.card-title {
  font-size: 1.5vh;
}

.card-text {
  font-size: 1.25vh;
}

.card-max {
  font-size: 2vh;
}

.hintButtons {
  background-color: transparent;
  color: #EBECD0;
}

:deep(.toggle-off) {
  background-color: #EBECD0;
  border-color: #EBECD0;
  outline: none;
}

:deep(.toggle-on) {
  background-color: #BB86FC;
  border-color: #BB86FC;
  outline: none;
}

:deep(.toggle-container, .toggle-container:focus, .toggle-container:active, .toggle-container::after, .toggle-container::before) {
  outline: none;
  border: none;
}

.kingIcon {
  margin-top: -1vh;
  width: 4vh;
  height: 5vh;
}



#clock {
  font-family: 'Share Tech Mono', sans-serif;
}

.bgr-dark {
  background-color: #1d1d1d;
}

.sidebar {
  background-color: #2e2e2e;
  color: #EBECD0;
  overflow: auto;
  margin-bottom: 0.5rem;
  min-width: 40vh;
}

:deep(.main-board) {
  position: relative;
  display: block;
  height: 0;
  padding-bottom: 100%;
  width: 100%;
  cursor: pointer;
}

:deep(cg-board) {
  background-image: url('@/assets/chessboard.svg') ;
  background-size: cover;
  box-shadow: 0 0 20px #BB86FC;
}

:deep(.orientation-white .files coord:nth-child(2n + 1)),
:deep(.orientation-white .ranks coord:nth-child(2n)),
:deep(.orientation-black .files coord:nth-child(2n)),
:deep(.orientation-black .ranks coord:nth-child(2n + 1)) {
  color: #EBECD0 ;
  font-family: 'Roboto', sans-serif ;
}

:deep(.orientation-white .files coord:nth-child(2n)),
:deep(.orientation-white .ranks coord:nth-child(2n + 1)),
:deep(.orientation-black .files coord:nth-child(2n + 1)),
:deep(.orientation-black .ranks coord:nth-child(2n)) {
  color: #BB86FC;
  font-family: 'Roboto', sans-serif ;
}

:deep(cg-board square.last-move) {
  will-change: transform;
  background-color: rgba(35, 2, 75, 0.3);
}
:deep(cg-board square.selected) {
  background-color: rgba(35, 2, 75, 0.3);
}

:deep(cg-board piece.dragging) {
  cursor: move;
}

:deep(cg-board square.move-dest) {
  background: radial-gradient(
    rgba(35, 2, 75, 0.4) 22%,
    rgb(56, 77, 65) 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}

:deep(cg-board square.premove-dest) {
  background: radial-gradient(
    rgba(20, 30, 85, 0.7) 22%,
    #203085 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}

:deep(cg-board square.oc.move-dest) {
  background: radial-gradient(
    transparent 0%,
    transparent 80%,
    rgba(35, 2, 75, 0.4) 80%
  );
}

:deep(cg-board .cg-square.move-dest.drag-over),
:deep(cg-board .cg-square.premove-dest.drag-over) {
  box-shadow: inset 0 0 10px 2px rgba(216, 85, 0, 0.9);
}

:deep(.cg-wrap) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important; 
  height: 100% !important;
}


@media (orientation: landscape) {
  .main-wrap {
    max-width: 100%; /* or whatever new max you prefer */
  }
}

</style>